import { useState, type ReactNode } from "react";
import {
  useChitChatsSetup,
  useSaveChitChatsSetup,
} from "@/api/chitchats-supply";
import { useSelfRatedSupply } from "@/api/self-rated-supply";
import type { CarrierPurchasingOption } from "@/api/carriers";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SelfRatedSupplySection } from "./self-rated-supply-section";
import { ProviderLabelSetupForm } from "./provider-label-setup-form";

function PurchasingOption({
  name,
  status,
  children,
  initiallyOpen = false,
}: {
  name: string;
  status: string;
  children: ReactNode;
  initiallyOpen?: boolean;
}) {
  return (
    <Collapsible defaultOpen={initiallyOpen} className="rounded-lg border p-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h4 className="font-medium">{name}</h4>
          <p className="text-sm text-muted-foreground">{status}</p>
        </div>
        <CollapsibleTrigger asChild>
          <Button
            type="button"
            variant="outline"
            aria-label={`Configure ${name}`}
          >
            Configure
          </Button>
        </CollapsibleTrigger>
      </div>
      <CollapsibleContent
        forceMount
        className="mt-4 data-[state=closed]:hidden"
      >
        <section aria-label={`${name} purchasing option`}>{children}</section>
      </CollapsibleContent>
    </Collapsible>
  );
}
export function ShippingPricesSection({
  carrierId,
  carrierName,
  carrierCode,
  channels,
}: {
  carrierId: string;
  carrierName: string;
  carrierCode: string;
  channels: CarrierPurchasingOption[];
}) {
  const local = useSelfRatedSupply(carrierId);
  const query = useChitChatsSetup(carrierId);
  const mutation = useSaveChitChatsSetup(carrierId);
  const [adding, setAdding] = useState(false);
  const supportsChitChats = carrierCode.toLowerCase() === "chitchats";
  const unsupportedConnection =
    !supportsChitChats &&
    (!!query.data?.supply ||
      channels.some((channel) => channel.apiProvider?.code === "chitchats"));
  const otherActive = channels.filter(
    (channel) =>
      channel.active &&
      channel.apiProvider &&
      (supportsChitChats || channel.apiProvider.code !== "chitchats") &&
      (channel.apiProvider.code !== "chitchats" ||
        query.isError ||
        (query.data?.supply && channel.id !== query.data.supply.id))
  );
  return (
    <section aria-label="Shipping prices & labels" className="space-y-3">
      <h3 className="font-medium">Shipping prices &amp; labels</h3>
      <p className="text-sm text-muted-foreground">
        Configure each purchasing option independently. The shipping carrier
        stays {carrierName}; each provider supplies prices and labels for the
        services it supports. Pickup and delivery operations are configured
        separately.
      </p>
      <a href="/carriers" className="text-sm underline">
        Manage integration accounts (API Providers)
      </a>
      <PurchasingOption
        name="EZX rate tables"
        status={
          local.isPending
            ? "Loading…"
            : local.isError
              ? "Unable to load"
              : local.data
                ? local.data.active
                  ? "Enabled"
                  : "Disabled"
                : "Not configured"
        }
      >
        <SelfRatedSupplySection
          carrierId={carrierId}
          carrierName={carrierName}
        />
      </PurchasingOption>
      {unsupportedConnection && (
        <section
          aria-label="Unsupported ChitChats connection"
          className="space-y-2 rounded-lg border p-4"
        >
          <h4 className="font-medium">Unsupported ChitChats connection</h4>
          <p className="text-sm">
            ChitChats provides only ChitChats services. This existing connection
            cannot be configured or activated here. Its saved data has been
            preserved.
          </p>
          <p className="text-sm">
            {query.data?.supply?.active ||
            channels.some(
              (channel) =>
                channel.active && channel.apiProvider?.code === "chitchats"
            )
              ? "The saved connection is enabled and needs administrator review. It has not been disabled automatically."
              : "The saved connection has not been changed."}
          </p>
        </section>
      )}
      {query.isPending ? (
        <p role="status">Loading label providers…</p>
      ) : query.isError ? (
        <div role="alert">
          <p>Unable to load label provider setup.</p>
          <Button type="button" onClick={() => void query.refetch()}>
            Retry label provider setup
          </Button>
        </div>
      ) : supportsChitChats &&
        query.data?.provider &&
        (query.data.supply || adding) ? (
        <PurchasingOption
          name="ChitChats"
          status={
            query.data.supply
              ? query.data.supply.active
                ? "Enabled"
                : "Disabled"
              : "Not saved"
          }
          initiallyOpen={adding}
        >
          <ProviderLabelSetupForm
            key={`${carrierId}:${query.data.supply?.updatedAt ?? "new"}`}
            carrierId={carrierId}
            carrierName={carrierName}
            setup={query.data}
            provider={query.data.provider}
            providerName="ChitChats"
            saveSetup={mutation.mutateAsync}
            isSaving={mutation.isPending}
            reload={() => void query.refetch()}
          />
        </PurchasingOption>
      ) : null}
      {supportsChitChats &&
        !query.isPending &&
        !query.isError &&
        !query.data?.supply &&
        !adding && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="outline">
                Add label provider
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem
                disabled={!query.data?.provider}
                onSelect={() => setAdding(true)}
              >
                ChitChats
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      {supportsChitChats &&
        !query.isPending &&
        !query.isError &&
        !query.data?.provider && (
          <p className="text-sm">
            Connect ChitChats in API Providers before adding it here.
          </p>
        )}
      <p className="text-sm text-muted-foreground">
        ChitChats setup is available only for the ChitChats carrier. It provides
        ChitChats services, not services for other carriers.
      </p>
      {otherActive.length > 0 && (
        <div className="space-y-2">
          <h4 className="font-medium">Other active purchasing options</h4>
          <p className="text-sm text-muted-foreground">
            These existing options are preserved. Editing them is not supported
            here yet; inactive options aren’t shown here.
          </p>
          {otherActive.map((channel) => (
            <div key={channel.id} className="rounded-lg border p-3">
              <span className="font-medium">{channel.apiProvider?.name}</span>
              <p className="text-sm text-muted-foreground">
                Enabled · Priority {channel.priority} ·{" "}
                {channel.credentialId
                  ? "Separate credential account"
                  : "EZX integration account"}
              </p>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
